/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { STATUSPROGRESS } from 'helpers/constants'
import { isEmpty } from 'lodash'
import { useSetRecoilState } from 'recoil'
import { openModalState } from 'recoils/atoms'

import { Button, Modal, PromptGuard } from 'components'
import {
  initialFormAssignTicket,
  schemaFormAssignTicket,
  IResponseTicket,
  formatUsers,
} from 'features/Ticket'

import TicketAssignDetail from './TicketAssignDetail'
import TicketAssignForm from './TicketAssignForm'
import { setFormatAssignDate } from 'helpers/common'
import { currentStatusProgressState } from 'recoils/atoms'

import { usePromptForm } from 'helpers/hooks'

import type {
  ISchemaFormAssignTicket,
  MapIUsersInfo,
  IUserSchema,
  ITransferReasonsSchema,
} from '../model'

interface IAssignModalProps {
  id: string
  isOpen: boolean
  ticket: IResponseTicket
  onClose: (e: any) => void
  onRefreshDetail: () => void
  payloadBeforeSubmit?: ISchemaFormAssignTicket | null
  setPayloadBeforeSubmit: (payload?: ISchemaFormAssignTicket | null) => void
  setConfirmModal: () => void
  setOpenAssignTicket: () => void
}

interface SetValueType {
  key: string
  value?: string | number | Date | IUserSchema[] | ITransferReasonsSchema[]
}

function TicketAssignModal(props: IAssignModalProps): JSX.Element {
  const { t } = useTranslation(['ticket', 'common'])

  const statusProgress = useRecoilValue(currentStatusProgressState)

  const [isStatusDone, setIsStatusDone] = useState<boolean>(false)
  const [isStatusInProgressCheckedIn, setIsStatusInProgressCheckedIn] =
    useState<boolean>(false)
  const [isStatusClosed, setIsStatusClosed] = useState<boolean>(false)
  const setIsOpenModal = useSetRecoilState(openModalState)

  const {
    isOpen,
    ticket,
    onClose,
    setConfirmModal,
    payloadBeforeSubmit,
    setPayloadBeforeSubmit,
    setOpenAssignTicket,
  } = props

  const {
    code,
    type,
    statusPriority,
    requestDate,
    users,
    assignStart,
    assignEnd,
    issueDetail,
  } = ticket

  const methods = useForm({
    defaultValues: initialFormAssignTicket,
    resolver: yupResolver(schemaFormAssignTicket),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const base = 'f-ticket-assign-modal'

  const setFormatDate = (date: string) => setFormatAssignDate(new Date(date))

  const assignDate = setFormatDate(requestDate)

  const [shouldPrompt, setShouldPrompt] = usePromptForm(methods)

  useEffect(() => {
    if (shouldPrompt || methods.formState.isSubmitted) {
      return
    }

    if (methods.formState.isDirty) {
      setShouldPrompt(true)
    }
  }, [methods.formState])

  const beforeSubmit = async (value: ISchemaFormAssignTicket) => {
    if (!isEmpty(value.users)) {
      setConfirmModal()
      setPayloadBeforeSubmit(value)

      await setTimeout(() => {
        setShouldPrompt(false)
      }, 2000)
    }
  }

  useEffect(() => {
    const _isStatusDone = statusProgress.toLowerCase() === STATUSPROGRESS.DONE
    const _isStatusInProgress =
      statusProgress.toLowerCase() === STATUSPROGRESS.IN_PROGRESS
    const _isStatusClosed =
      statusProgress.toLowerCase() === STATUSPROGRESS.CLOSED

    _isStatusDone ? setIsStatusDone(true) : setIsStatusDone(false)

    _isStatusInProgress &&
      ticket?.checkInAt &&
      setIsStatusInProgressCheckedIn(true)

    _isStatusClosed && setIsStatusClosed(true)
  }, [statusProgress])

  useEffect(() => {
    if (isEmpty(payloadBeforeSubmit) && users) {
      methods.reset({
        ...initialFormAssignTicket,
        assignStart: assignStart
          ? new Date(assignStart)
          : new Date(new Date().setHours(8, 0, 0)),
        assignEnd: assignEnd
          ? new Date(assignEnd)
          : new Date(new Date().setHours(9, 0, 0)),
        assignDate: assignStart ? new Date(assignStart) : new Date(),
        ...(users && { users: formatUsers(users) as MapIUsersInfo[] }),
        statusPriority,
        issueDetail: issueDetail || '',
      })
    }
  }, [users])

  useEffect(() => {
    if (!isEmpty(payloadBeforeSubmit)) setupDefaultValueBeforeSubmit()
  }, [])

  const _setValue = ({ key, value }: SetValueType) =>
    methods.setValue(key, value, {
      shouldDirty: true,
    })

  const setupDefaultValueBeforeSubmit = () => {
    _setValue({ key: 'assignStart', value: payloadBeforeSubmit?.assignStart })
    _setValue({ key: 'assignEnd', value: payloadBeforeSubmit?.assignEnd })
    _setValue({ key: 'assignDate', value: payloadBeforeSubmit?.assignDate })
    _setValue({ key: 'issueDetail', value: payloadBeforeSubmit?.issueDetail })
    _setValue({ key: 'users', value: payloadBeforeSubmit?.users })
    _setValue({
      key: 'statusPriority',
      value: payloadBeforeSubmit?.statusPriority,
    })
  }

  const handleCloseModal = () => {
    setIsOpenModal(false)

    if (shouldPrompt) {
      onClose(methods.watch())
    } else {
      setOpenAssignTicket()
      setPayloadBeforeSubmit(null)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      isIcon={false}
      isFilledModal
    >
      <div id='assign-content-form' className={`${base}__content`}>
        <div className={`${base}__modal-header`}>
          <span className={`${base}__modal-header--title`}>
            {t('ticket:ticketId.title')} {code || '-'}
          </span>
        </div>

        <TicketAssignDetail type={type.nameTh} assignDate={assignDate} />

        <FormProvider {...methods}>
          <PromptGuard when={shouldPrompt} />

          <form
            className={`${base}__form`}
            onSubmit={methods.handleSubmit(beforeSubmit)}
          >
            <TicketAssignForm users={users} statusPriority={statusPriority} />

            <div className={`${base}__container-button`}>
              <Button
                className={`${base}__button--action-button`}
                variants='secondary'
                onClick={handleCloseModal}
              >
                {t('common:cancel.button')}
              </Button>
              <Button
                className={`${base}__button--action-button`}
                type='submit'
                disabled={
                  !statusProgress ||
                  isStatusDone ||
                  isStatusInProgressCheckedIn ||
                  isStatusClosed
                }
              >
                {t('ticket:appointment.button')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}

export default TicketAssignModal
